import dynamic from 'next/dynamic';
import React, { ComponentType } from 'react';
import { weakKey } from '~/shared/utils/jsx';
import { useProductDetailsStore } from '../../hooks/useProductDetailsStore';
import { ToastContainer } from 'react-toastify';
import styled from '@emotion/styled';
import { theme } from '~/theme';
import { breakpoints } from '~/theme/breakpoints';

export type VariationAttributesValues = {
    className: string;
    disabled: boolean;
    href?: string;
    id?: string;
    image?: {
        alt: string;
        color: string;
        orderable?: boolean;
        selected?: boolean;
        supplierColor: string;
        title: string;
        url: string;
    };
    label: string;
    name: string;
    orderable: boolean;
    value: string;
    _type: string;
};

export type VariationAttributes = {
    id: string;
    name: string;
    values: VariationAttributesValues[];
    selectedValue?: { name: string; value: string };
    _type: string;
};

type Props = {
    variations?: VariationAttributes[];
    onClick?: (payload: any) => void;
    isLoading?: boolean;
};

export interface VariantPickerProps {
    variant: VariationAttributes;
    selectedAttributes?: Record<string, string | number> | null;
    key: string;
    id: string;
    onClick?: (payload: VariationAttributesValues) => void;
    isLoading?: boolean;
}

interface VariantPickerTypes {
    [key: string]: ComponentType<VariantPickerProps>;
}

const VariantPickers: VariantPickerTypes = {
    color: dynamic(() => import('./components/VariantList')),
    supplierColour: dynamic(() => import('./components/VariantList')),
    default: dynamic(() => import('./components/VariantDropdown')),
};

const StyledToastContainer = styled(ToastContainer)(() => ({
    ['&.Toastify__toast-container']: {
        position: 'absolute',
        inset: `auto ${theme.space[3]}`,
        padding: 0,
        transform: 'translateY(-100%)',
        zIndex: 1,
        overflow: 'hidden',
        width: 'auto',
        [breakpoints.sm]: {
            inset: 'auto',
            width: '100%',
        },
    },
}));

export const PDPVariants = ({ variations, onClick, isLoading }: Props) => {
    const { product } = useProductDetailsStore((state) => ({
        product: state.product,
    }));

    return (
        <>
            {variations?.map((variation: VariationAttributes) => {
                const VariantPicker =
                    (variation.id && VariantPickers[variation.id]) || VariantPickers.default;
                return (
                    <div key={weakKey(variation)}>
                        <VariantPicker
                            key={variation.id}
                            id={variation.id}
                            onClick={onClick}
                            selectedAttributes={product?.variationValues}
                            variant={variation}
                            isLoading={isLoading}
                        />
                    </div>
                );
            })}

            <StyledToastContainer containerId="toastvariantcontainer" enableMultiContainer />
        </>
    );
};
